import { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { SCHOOL } from "../data/content";
import { Logo } from "./Navbar";

const SOCIALS = [
  { href: "https://www.facebook.com/eletech.ca", label: "EGOS on Facebook", tid: "footer-social-facebook", Icon: Facebook },
  { href: "https://www.instagram.com/eletech_global_ontario_school/", label: "EGOS on Instagram", tid: "footer-social-instagram", Icon: Instagram },
];

const COLS = [
  {
    title: "School",
    links: [
      { to: "/", label: "Home" },
      { to: "/why-egos", label: "Why EGOS" },
      { to: "/courses", label: "OSSD Courses" },
      { to: "/process", label: "Admission Process" },
      { to: "/demo", label: "Demo" },
      { to: "/tuition", label: "Tuition & Fees" },
      { to: "/international", label: "International Students" },
      { to: "/faq", label: "FAQ" },
      { to: "/blog", label: "Blog" },
    ],
  },
  {
    title: "Reach Out",
    links: [
      { to: "/admissions", label: "Apply" },
      { to: "/contact", label: "Contact" },
    ],
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    try {
      await api.post("/contact", { name: "Newsletter subscriber", email, subject: "Newsletter", message: `Newsletter subscription request from ${email}` });
      toast.success("You are on the list — welcome to Eletech.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <footer className="bg-ink text-ivory" data-testid="site-footer">
      <div className="container-x border-b border-ivory/10 pb-14 pt-16">
        <p className="eyebrow !text-brandlight">Ready when you are</p>
        <h2 className="mt-4 max-w-3xl font-display text-4xl font-extrabold tracking-tight text-ivory sm:text-6xl">
          Ready to earn your <span className="font-serif italic font-medium text-brand">OSSD?</span>
        </h2>
        <Link to="/admissions" data-testid="footer-giant-cta" className="btn-accent mt-8">Apply in 5 minutes</Link>
      </div>
      <div className="container-x grid gap-12 py-16 md:grid-cols-2 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <Logo light slim tid="footer-logo" />
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-ivory/60">
            A ministry-inspected private online high school serving students across Ontario, Canada and around the world
            with OSSD credits taught by Ontario Certified Teachers.
          </p>
          <p className="mt-5 font-mono text-xs uppercase tracking-[0.2em] text-ivory/40">
            Inspected and Authorized by Ministry of Education, Ontario
          </p>
          <div className="mt-5 flex items-center gap-3" data-testid="footer-social-links">
            {SOCIALS.map(({ href, label, tid, Icon }) => (
              <a
                key={tid}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                title={label}
                data-testid={tid}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-ivory/20 text-ivory/70 transition-colors hover:border-brand hover:text-brand"
              >
                <Icon size={18} />
              </a>
            ))}
          </div>
          <p className="mt-7 font-mono text-xs uppercase tracking-[0.25em] text-brandlight">Helpful resources</p>
          <ul className="mt-4 space-y-3">
            <li>
              <a
                href="https://www.ouac.on.ca/"
                target="_blank"
                rel="noopener"
                data-testid="footer-ext-ouac"
                className="text-sm text-ivory/70 transition-colors hover:text-brand"
              >
                OUAC — Ontario Universities' Application Centre ↗
              </a>
            </li>
            <li>
              <a
                href="https://www.ontario.ca/page/ministry-education"
                target="_blank"
                rel="noopener"
                data-testid="footer-ext-ministry"
                className="text-sm text-ivory/70 transition-colors hover:text-brand"
              >
                Ontario Ministry of Education ↗
              </a>
            </li>
          </ul>
        </div>

        {COLS.map((col) => (
          <div key={col.title}>
            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-brandlight">{col.title}</h3>
            <ul className="mt-5 space-y-3">
              {col.links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-sm text-ivory/70 transition-colors hover:text-brand">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div>
          <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-brandlight">Stay in the loop</h3>
          <p className="mt-5 text-sm text-ivory/60">Course launches, guidance tips and application deadlines.</p>
          <form onSubmit={subscribe} className="mt-4 flex gap-2" data-testid="footer-newsletter-form">
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              data-testid="footer-newsletter-input"
              className="w-full rounded-full border border-ivory/20 bg-ivory/5 px-4 py-2.5 text-sm text-ivory placeholder:text-ivory/40 outline-none focus:border-brand"
            />
            <button type="submit" data-testid="footer-newsletter-submit" className="rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white transition hover:bg-amber-600">
              Join
            </button>
          </form>
        </div>
      </div>

      <div className="border-t border-ivory/10">
        <div className="container-x flex flex-col items-center justify-between gap-3 py-6 sm:flex-row">
          <p className="text-xs text-ivory/50">© {new Date().getFullYear()} {SCHOOL.name}. All rights reserved.</p>
          <div className="flex items-center gap-6 text-xs text-ivory/50">
            <Link to="/privacy" className="hover:text-ivory">Privacy Policy</Link>
            <Link to="/refund-policy" className="hover:text-ivory">Refund Policy</Link>
            <Link to="/admin" data-testid="footer-admin-link" className="hover:text-ivory">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
